import type { ReactNode } from "react";
import { Navigate } from "react-router";
import { PageLoader } from "@/components/ui/PageLoader";
import { useAuth } from "@/data/AuthProvider";
import { useEnsureProfile } from "@/data/useEnsureProfile";
import { VerifyEmail } from "@/screens/user/VerifyEmail";

/**
 * The 2020 PrivateRoute returned `null` while auth was resolving, which blanked
 * the page on every hard refresh. Render a loader instead, and only redirect
 * once we actually know the user is signed out.
 *
 * Signed in is not sufficient — the address has to be verified too. An account
 * created with a password is usable the instant it exists, so without this any
 * address that can be typed becomes an account, whether or not it is real or
 * belongs to whoever typed it.
 *
 * `VerifyEmail` is rendered in place of the screen rather than redirected to,
 * so the gate holds on every protected route at once and there is no verify
 * URL to navigate away from. This is the courtesy half of the control: the
 * enforcing half is `firestore.rules`, which rejects an unverified write
 * outright. A client-side gate alone stops nobody holding the API key — which
 * is public, by design.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const auth = useAuth();

  // Every way into the app passes through here, which is the point: the profile
  // write is skipped at sign-up (the rules reject an unverified write) and
  // cannot be pinned to any one button afterwards, because verification happens
  // in another tab and the user may just reload. Hooks cannot be called
  // conditionally, so this runs on every render and no-ops unless it is needed.
  useEnsureProfile(auth.status === "signedIn" ? auth.user : undefined);

  if (auth.status === "loading") {
    return (
      <PageLoader />
    );
  }

  if (auth.status === "signedOut") {
    return <Navigate to="/user/sign-in" replace />;
  }

  if (!auth.user.emailVerified) {
    return <VerifyEmail user={auth.user} />;
  }

  return <>{children}</>;
}
